// Shared helpers for rendering EDAP samples on a chart, used by both the live
// (SSE) view and the historical view so the two stay visually consistent.

// How many samples fill the visible live graph — adaptive to the sample rate so fast
// streams show many points and slow ones show few. Effectively "the last ~LIVE_WINDOW_MS
// of data", clamped to a sane point count (~30 at 1s, floored at 6 for >=5s/minutes).
const LIVE_WINDOW_MS = 30 * 1000;
const LIVE_MIN_POINTS = 6;
const LIVE_MAX_POINTS = 40;

export const liveVisiblePoints = intervalMs => {
    if (!intervalMs) {
        return LIVE_MIN_POINTS;
    }
    return Math.min(Math.max(Math.round(LIVE_WINDOW_MS / intervalMs), LIVE_MIN_POINTS), LIVE_MAX_POINTS);
};

// Upper bound on the live rolling buffer (max visible points + headroom for scroll-off).
export const LIVE_BUFFER_MAX = LIVE_MAX_POINTS + 15;

// Raw samples carry a `time`/`timestamp` string; 15-minute aggregates carry `aggregation_window`
// plus a `time: { first, last }` object, so pick the window (or the object's `last`) for those.
export const getTimestamp = sample => {
    const t =
        sample.timestamp ??
        sample.aggregation_window ??
        sample.group ??
        (typeof sample.time === 'string' ? sample.time : sample.time?.last ?? sample.time?.first);
    return t ? new Date(t) : null;
};

// Pull a plottable number from a value that may be a raw number, a boolean (→ 0/1), or an
// aggregate bucket `{ count, min, avg, max }` (→ its avg). Returns null when not chartable.
const numericValue = value => {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (value && typeof value === 'object' && typeof value.avg === 'number') {
        return value.avg;
    }
    return null;
};

// Raw samples expose a numeric `power`/`power_avg`; aggregates expose `power: { avg, min, max }`.
export const getPower = sample => numericValue(sample.power_avg ?? sample.power);

const sampleTime = sample => {
    const date = getTimestamp(sample);
    return date ? date.getTime() : NaN;
};

// Median gap (ms) between recent samples — the approximate live sample rate. Returns null
// when there aren't enough samples to tell.
export const medianInterval = samples => {
    if (!samples || samples.length < 2) {
        return null;
    }
    const times = samples
        .slice(-12)
        .map(sampleTime)
        .filter(t => !Number.isNaN(t))
        .sort((a, b) => a - b);
    const diffs = [];
    for (let i = 1; i < times.length; i++) {
        diffs.push(times[i] - times[i - 1]);
    }
    if (!diffs.length) {
        return null;
    }
    diffs.sort((a, b) => a - b);
    return diffs[Math.floor(diffs.length / 2)] || null;
};

// Human-readable sample rate, e.g. "~5 sec", "~1 min", "~2 min".
export const formatRate = intervalMs => {
    if (!intervalMs) {
        return '—';
    }
    const secs = Math.round(intervalMs / 1000);
    if (secs < 60) {
        return `~${secs} sec`;
    }
    const mins = Math.round(secs / 60);
    return `~${mins} min`;
};

// Colors assigned to sensor series by the order they appear in the sample, so any set of
// sensor keys gets stable, distinct colors without a per-key map.
const SENSOR_COLORS = ['#f5a623', '#14b8a6', '#8b5cf6', '#94a3b8', '#cbd5e1', '#ef4444', '#22c55e'];

// "power_solar" -> "Power solar". No curated names — just a readable version of the raw key.
const prettifyKey = key => key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

// A sensor is charted when its values are numeric, boolean, or aggregate buckets (plotted as
// their avg). Booleans plot as 0/1; strings/enums, arrays, and aggregated booleans (which have
// no avg) are dropped. Returns 'number' | 'boolean', or null when the key can't be charted.
const sensorChartType = (samples, key) => {
    const values = samples.map(sample => sample.sensors?.[key]).filter(v => v != null);
    if (!values.length) {
        return null;
    }
    if (values.every(v => typeof v === 'boolean')) {
        return 'boolean';
    }
    if (values.every(v => numericValue(v) != null)) {
        return 'number';
    }
    return null;
};

// Build CanvasJS line series from a list of samples. `markerSize` > 0 draws a dot at each data
// point (used for the sparse live view; the dense historical view leaves it 0).
export const buildChartSeries = (samples, markerSize = 0) => {
    // Union of keys across all samples so anything the gateway sends gets a series, even if it
    // isn't present in the first sample. Sorted so the order (and thus colors + legend) stays
    // stable — samples list their sensor keys in varying order, so an unsorted union would
    // reshuffle the series on every rebuild. Keys that can't be plotted (strings/arrays) are dropped.
    const sensorKeys = [...new Set(samples.flatMap(sample => Object.keys(sample.sensors || {})))].sort();
    const plotted = sensorKeys.map(key => ({ key, type: sensorChartType(samples, key) })).filter(d => d.type);

    const seriesDefs = [
        { name: 'Consumption (kW)', color: '#2c5cff', get: getPower, visible: true },
        ...plotted.map(({ key, type }, i) => ({
            // Booleans are labelled (0/1) since they plot on the same axis as kW values.
            name: prettifyKey(key) + (type === 'boolean' ? ' (0/1)' : ''),
            color: SENSOR_COLORS[i % SENSOR_COLORS.length],
            get: sample => numericValue(sample.sensors?.[key]),
            // Only the kW power flows (`power_solar`, `power_battery`, …) are shown by default so
            // the chart isn't dominated by frequency/SoC/boolean/config series; everything else
            // stays one legend-click away. `^power_` excludes config values like
            // `max_charging_power_kw`.
            visible: /^power_/.test(key),
        })),
    ];

    return seriesDefs.map(def => ({
        type: 'line',
        name: def.name,
        color: def.color,
        showInLegend: true,
        visible: def.visible,
        xValueType: 'dateTime',
        // Tooltip header uses the series format (not the axis one), which defaults to
        // date-only — set it so the tooltip shows the time too (samples are seconds apart).
        xValueFormatString: 'MMM DD HH:mm:ss',
        markerSize,
        markerType: 'circle',
        dataPoints: samples.map(sample => ({ x: getTimestamp(sample), y: def.get(sample) ?? null })).filter(point => point.x),
    }));
};
